import { useState, useCallback } from 'react';
import type { DocumentInfo } from '../types/document.js';
import {
  FILE_INPUT_ACCEPT,
  IS_COARSE_POINTER,
  getDocumentType,
  getDocumentTypeFromName,
} from '../types/document.js';
import { useAuthFetch } from '../hooks/useAuthFetch.js';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt.js';
import { usePlatform } from '../contexts/PlatformContext.js';
import { UpgradeDialog } from './UpgradeDialog.js';

export interface DocumentUploadProps {
  onDocumentLoaded: (doc: DocumentInfo) => void;
  /**
   * `panel` (default) is the full dropzone shown when nothing is open.
   *
   * `inline` is a single button sized to sit in the zoom row alongside Close,
   * so opening another document does not require closing the current one
   * first. That coupling was the reason Close had to carry follow-mode
   * semantics at all: on a phone, "Choose a file" could only be reached by
   * closing, and closing while following is a contradiction. Two attempts to
   * reconcile the two each shipped a worse bug (2026-08-27); separating them
   * removes the conflict instead of arbitrating it.
   */
  variant?: 'panel' | 'inline';
}

const SERVER_URL = import.meta.env.VITE_SERVER_URL || '';

/**
 * localStorage, NOT sessionStorage.
 *
 * sessionStorage is wiped when the WebView context itself is destroyed, which
 * is precisely the event these counters exist to detect -- so they reset in
 * lockstep with the thing they were measuring and reported "mounts: 1,
 * lastUpload: none" through every occurrence. A perfectly self-concealing
 * instrument, and it cost two rounds on 2026-08-31.
 *
 * localStorage survives that, so the counter only ever climbs. It is not reset
 * per meeting and is not meant to be: what matters is whether it moves across
 * one file-picker interaction.
 */
/**
 * Record how the last upload ended, where the state readout can show it.
 *
 * sessionStorage rather than React state on purpose: it outlives a WebView
 * reload, and a reload is one of the things we are trying to detect. On a phone
 * the inline error popover is easy to miss entirely, so "the upload quietly
 * failed" and "the upload worked and was then overwritten" look identical from
 * across a room. They are not the same bug and must not be diagnosed together.
 */
function noteUpload(outcome: string) {
  try {
    window.localStorage.setItem('annoteer.lastUpload', outcome);
  } catch {
    // Diagnostics must never break an upload.
  }
}

function IconOpen() {
  // A page with a plus — "bring in another document", distinct from the
  // download arrow and from the sparkle that means AI.
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 1.5H4a1 1 0 00-1 1v11a1 1 0 001 1h5" />
      <path d="M9 1.5L12.5 5v3.5" />
      <path d="M12 10.5v4M10 12.5h4" />
    </svg>
  );
}

export function DocumentUpload({ onDocumentLoaded, variant = 'panel' }: DocumentUploadProps) {
  const { authFetch, isAuthenticated, status: authStatus } = useAuthFetch();
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('Uploading...');
  const [error, setError] = useState<string | null>(null);
  const upgrade = useUpgradePrompt();
  const meetingId = usePlatform().context?.meetingId;

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      // Breadcrumb FIRST, before any early return. The previous one sat after
      // the type check, so "the change event never fired" and "it fired and
      // returned nothing" both showed as lastUpload: none -- which is exactly
      // where the 2026-08-31 diagnosis stalled.
      const count = event.target.files?.length ?? -1;
      const file = event.target.files?.[0];
      noteUpload(`change n=${count}${file ? ' ' + file.name.slice(-14) : ''}`);
      if (!file) {
        // Silence here is what made this look like a dead button.
        setError(
          'The file picker returned no file. Check that Teams has permission to access files on this device.',
        );
        return;
      }

      // Fall back to the file NAME when the device gives us no usable type.
      // Android reports an empty type or application/octet-stream for files
      // from Downloads or Drive, and the old check only had an extension
      // fallback for Office formats — so an ordinary PDF or photo was rejected
      // right here and never uploaded at all.
      //
      // Only a decision about whether to ATTEMPT the upload. The server
      // re-derives the type from the bytes and refuses anything the content
      // does not support, so a misleading name buys nothing.
      const docType = getDocumentType(file.type) ?? getDocumentTypeFromName(file.name);
      if (!docType) {
        noteUpload(`rejected type "${file.type || 'none'}" ${file.name.slice(-12)}`);
        setError('Unsupported file type. Upload a PDF, image, PowerPoint, Word, or Excel file.');
        return;
      }

      const isPresentation = docType === 'presentation';
      setUploading(true);
      setUploadStatus(isPresentation ? 'Converting presentation...' : 'Uploading...');
      setError(null);

      try {
        const formData = new FormData();
        // Before the file: multer only populates req.body with fields that
        // precede it. The server records the first uploader's organisation as
        // the meeting host, which is how it tells guests apart.
        if (meetingId) formData.append('meetingId', meetingId);
        formData.append('file', file);

        const response = await authFetch(`${SERVER_URL}/api/documents/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          // A 402 is a payment gate, not a failure the user can fix by
          // retrying — show the upgrade prompt instead of a red error.
          if (await upgrade.handleResponse(response)) {
            setUploading(false);
            event.target.value = '';
            return;
          }
          const errorBody = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(errorBody?.error ?? `Upload failed: ${response.statusText}`);
        }

        const result = (await response.json()) as {
          documentId: string;
          mimeType: string;
          documentType: 'pdf' | 'image' | 'presentation' | 'document' | 'spreadsheet';
          base64: string;
        };

        const binary = atob(result.base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        noteUpload('ok ' + result.documentId.slice(0, 8));
        onDocumentLoaded({
          documentId: result.documentId,
          fileName: file.name,
          mimeType: result.mimeType,
          documentType: result.documentType,
          data: bytes,
        });
      } catch (err) {
        noteUpload('FAILED ' + (err instanceof Error ? err.message : 'unknown'));
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    // `upgrade` is stable (useCallback-backed), but listed so the dependency
    // set stays honest — see the authFetch stale-closure history.
    [onDocumentLoaded, authFetch, upgrade, meetingId],
  );

  // Opening a document cannot work on Teams for Android: the file picker is an
  // external activity, Teams destroys the WebView when it takes the foreground,
  // and the <input> that would deliver the file dies with it. Measured on a
  // Galaxy S10+ 2026-08-31 -- mounts went 1 -> 2 across one attempt while the
  // change event never fired.
  //
  // So do not offer the control. A meeting has one shared document that someone
  // opens on desktop and everyone annotates; a phone participant annotating it
  // is the whole of the supported mobile experience, and the listing already
  // says mobile is not a supported client. Offering a button that cannot work
  // is worse than not offering one -- it read as a broken app for two days.
  if (IS_COARSE_POINTER) {
    return variant === 'inline' ? null : (
      <div className="document-upload">
        <div className="document-upload__dropzone">
          <p>Waiting for a shared document</p>
          <p className="document-upload__status">
            Ask a participant on Teams for Windows, macOS or the web to open a document and press{' '}
            <strong>Present</strong>. It appears here as soon as they do, and everyone in the
            meeting — including this phone — can annotate it together.
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    // A <label> wrapping the input rather than a button that forwards a click:
    // tapping a label is the one gesture every mobile WebView honours for a
    // file input, and it needs no ref plumbing.
    const title = uploading ? uploadStatus : 'Open another document';
    return (
      <span className="document-upload document-upload--inline" data-auth-ready={isAuthenticated}>
        <label
          className={`document-upload__inline-btn${uploading ? ' document-upload__inline-btn--busy' : ''}${error ? ' document-upload__inline-btn--error' : ''}`}
          title={title}
          aria-label={title}
        >
          <IconOpen />
          <input
            type="file"
            accept={FILE_INPUT_ACCEPT}
            onClick={(e) => {
              noteUpload(`tap auth=${authStatus}`);
              // Deliberately NOT `disabled` when unauthenticated. A disabled
              // input swallows the tap, fires no event and -- with no disabled
              // styling -- looks identical to a working button. That is a
              // silently dead control, and it is indistinguishable from the
              // bug we spent 2026-08-30/31 chasing. Explain instead.
              if (!isAuthenticated) {
                e.preventDefault();
                setError(
                  authStatus === 'initializing'
                    ? 'Still signing in to Microsoft Teams. Try again in a moment.'
                    : 'Not signed in. Reopen Annoteer in the meeting to sign in again.',
                );
              }
            }}
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>
        {/* Anchored under the button rather than inline: this sits in a 24px
            row, so anything with real height has to leave the flow. Errors get
            a visible message and not just a tooltip — a tooltip is unreachable
            on a touch device, which is the client this control exists for. */}
        {(error || upgrade.reason) && (
          <div className="document-upload__inline-popover">
            {upgrade.reason ? (
              <UpgradeDialog reason={upgrade.reason} onClose={upgrade.clear} />
            ) : (
              <p className="document-upload__inline-error" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
                  &times;
                </button>
              </p>
            )}
          </div>
        )}
      </span>
    );
  }

  return (
    <div className="document-upload" data-auth-ready={isAuthenticated}>
      <div className="document-upload__dropzone">
        {!isAuthenticated ? (
          <p className="document-upload__status">
            {authStatus === 'initializing'
              ? 'Authenticating...'
              : 'Not signed in. Reopen Annoteer in the meeting to sign in again.'}
          </p>
        ) : (
          <>
            <p>Upload a document to start annotating</p>
            <input
              type="file"
              accept={FILE_INPUT_ACCEPT}
              onClick={() => noteUpload(`tap auth=${authStatus}`)}
              onChange={handleFileChange}
              disabled={uploading}
            />
          </>
        )}
        {uploading && <p className="document-upload__status">{uploadStatus}</p>}
        {error && <p className="document-upload__error">{error}</p>}
      </div>
      {upgrade.reason && <UpgradeDialog reason={upgrade.reason} onClose={upgrade.clear} />}
    </div>
  );
}
